import { MapContainer, TileLayer, GeoJSON, useMap, Pane } from "react-leaflet";
import L from "leaflet";
import seg from "../../utils/seg.json";
import { useEffect, useState, useMemo, useCallback } from "react";
import './styles.css'
import NotDataMap from "../Cards/NotDataDisplay";
import useIsMobile from "../../hooks/useIsMobile";
import { MdArrowDropDown, MdArrowDropUp } from "react-icons/md";
import { messages } from "../../utils/const";
import { colorsLayout } from "./utils";
import ResponsiveContainer from "../../layouts/ResponsiveContainer";
import MapLayers from "./MapLayers";
import { IMaps } from "../../interfaces/Maps";
import LayersActive from './LayersActive';
import { parseEncabezado } from "../../views/IndicatorDisplay/components/Graphics/Filters/helpers";

const capitalizeKey = (key: string) => key.charAt(0).toUpperCase() + key.slice(1);

const buildAdditionalInfoContent = (
    comunaNumber: string | number,
    info: Record<string, number | string>
) => {
    const rows = Object.entries(info)
        .filter(([key]) => key !== "comuna" && key !== "anio")
        .map(([key, value]) => `<div><strong>${key}:</strong><br/>${value}</div>`)
        .join("");

    return `<div class="comuna-info-tooltip">
        <div class="comuna-title"><strong>Comuna ${comunaNumber}</strong></div>
        <div class="divisor"></div>
        <div class="rows-map"> 
        ${rows}
        </div>
    </div>`;
};

const calculateColor = (value: number, intervals: number[], colors: string[]) => {
    if (value < intervals[0]) return colors[0];
    for (let i = 0; i < intervals.length - 1; i++) {
        if (value >= intervals[i] && value < intervals[i + 1]) {
            return colors[i];
        }
    }
    return colors[colors.length - 1];
};

const Legend = ({ intervals, colors }: { intervals: number[], colors: string[] }) => {
    const [openLegend, setOpenLegend] = useState(false);
    const isMobile = useIsMobile();

    useEffect(() => {
        setOpenLegend(!isMobile);
    }, [isMobile]);

    const renderLabels = () => {
        return intervals.map((interval, index) => {
            const nextInterval = intervals[index + 1];
            const label = nextInterval !== undefined
                ? `${interval.toFixed(0)} - ${(nextInterval - 1).toFixed(0)}`
                : `${interval.toFixed(0)} o +`;

            return (
                <div key={index} className="flex items-center mb-1">
                    <div className="w-4 h-4 mr-2" style={{ backgroundColor: colors[index] }} />
                    <span>{label}</span>
                </div>
            );
        });
    };

    return (
        <div className="legend text-bmedium md:text-llarge shadow-custom-dual" style={{ position: "absolute", top: "20px", right: "20px", background: "white", padding: "10px", borderRadius: "4px", zIndex: 500 }}>
            <div
                className={`flex justify-between items-center ${isMobile ? 'cursor-pointer' : ''}`}
                onClick={() => { if (isMobile) setOpenLegend((prev) => !prev); }}
            >
                <p className="font-semibold">Referencias</p>
                {isMobile && (
                    <button className="bg-transparent outline-none w-5 h-5 flex items-center justify-center">
                        {openLegend ? <MdArrowDropUp /> : <MdArrowDropDown />}
                    </button>
                )}
            </div>
            <div
                className={`grid transition-[grid-template-rows] duration-300 ease-in-out ${openLegend ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'}`}
            >
                <div className="overflow-hidden">
                    <div className="mt-2">{renderLabels()}</div>
                </div>
            </div>
        </div>
    );
};

const ComunaInfoModal = ({
    comunaNumber,
    info,
    onClose,
}: {
    comunaNumber: string | number;
    info: Record<string, number | string>;
    onClose: () => void;
}) => (
    <div className="comuna-info-modal-backdrop" onClick={onClose}>
        <div className="comuna-info-modal" onClick={(e) => e.stopPropagation()}>
            <button className="comuna-info-modal-close" onClick={onClose} aria-label="Cerrar">✕</button>
            <div dangerouslySetInnerHTML={{ __html: buildAdditionalInfoContent(comunaNumber, info) }} />
        </div>
    </div>
);

const MapController = ({ isMobile }: { isMobile: boolean }) => {
    const map = useMap();

    useEffect(() => {
        if (!map) return;
        const center = map.getCenter();
        const zoom = isMobile ? 11 : 11.5;
        map.setView(center, zoom);
        map.invalidateSize();
    }, [isMobile, map]);

    useEffect(() => {
        const updateFontSize = () => {
            const zoom = map.getZoom();
            const container = map.getContainer();
            const screenWidth = container.clientWidth;
            let baseSize = screenWidth < 640 ? 16 : 20;
            const newSize = Math.min(Math.max(baseSize * (zoom / 13), 4), 20);

            const styleId = 'leaflet-heatmap-font';
            let styleEl = document.getElementById(styleId);
            if (!styleEl) {
                styleEl = document.createElement('style');
                styleEl.id = styleId;
                document.head.appendChild(styleEl);
            }
            styleEl.innerHTML = `.comuna-tooltip { font-size: ${newSize}px !important; }`;
        };

        map.on('zoomend', updateFontSize);
        map.on('resize', updateFontSize);
        updateFontSize();

        return () => {
            map.off('zoomend', updateFontSize);
            map.off('resize', updateFontSize);
            document.getElementById('leaflet-heatmap-font')?.remove();
        };
    }, [map]);

    return null;
};

const HeatMap = ({ data, mapRef, errorsNoData, scale, chartId, mapLayer, activeCapaIds, additionalInfo, dynamicInfo }: IMaps) => {

    const isMobile = useIsMobile();
    const intervals = useMemo(() => {
        if (!data || !data.length) return [];
        const values = data.map((d) => parseFloat(d.valor || "0"));
        const min = Math.min(...values);
        const max = Math.max(...values);
        const step = (max - min) / 5;

        return [min, min + step, min + step * 2, min + step * 3, min + step * 4, max];
    }, [data]);

    const dataByComuna = useMemo(() => {
        const map = new Map<string, typeof data[number]>();
        data?.forEach((item) => {
            if (item.comuna) map.set(item.comuna, item);
        });
        return map;
    }, [data]);

    const additionalInfoByComuna = useMemo(() => {
        const map = new Map<string, Record<string, number | string>>();
        additionalInfo?.forEach((item) => {
            const comuna = item.comuna?.toString();
            if (comuna) map.set(comuna, item);
        });
        return map;
    }, [additionalInfo]);

    const dynamicInfoByComuna = useMemo(() => {
        const map = new Map<string, Record<string, string>>();
        dynamicInfo?.forEach((item) => {
            const comuna = parseEncabezado(item.encabezado)["Comuna"];
            if (comuna) map.set(comuna, item);
        });
        return map;
    }, [dynamicInfo]);

    const [colors, setColors] = useState<string[]>(colorsLayout);
    const [selectedComunaInfo, setSelectedComunaInfo] = useState<{
        comunaNumber: string | number;
        info: Record<string, number | string>;
    } | null>(null);

    useEffect(() => {
        if (scale === 0) {
            setColors([...colorsLayout].reverse());
        } else {
            setColors([...colorsLayout]);
        }
    }, [scale]);

    const styleFeature = useCallback((feature: any) => {
        const comunaID = feature.properties.COMUNAS;
        const comunaData = dataByComuna.get(comunaID.toString());

        if (!comunaData || !comunaData.valor || intervals.length === 0) {
            return {
                fillColor: "#FFEDA0",
                weight: 2,
                opacity: 1,
                color: "white",
                dashArray: "3",
                fillOpacity: 0.7,
            };
        }

        const value = parseFloat(comunaData.valor);
        const color = calculateColor(value, intervals, colors);

        return {
            fillColor: color,
            weight: 2,
            opacity: 1,
            color: "white",
            dashArray: "3",
            fillOpacity: 0.7,
        };
    }, [dataByComuna, intervals, colors]);

    const onEachFeature = useCallback((feature: any, layer: any) => {
        if (feature.properties && feature.properties.COMUNAS) {
            const comunaNumber = feature.properties.COMUNAS;

            let center = layer.getBounds().getCenter();

            if (comunaNumber === "1" || comunaNumber === 1) {
                center = L.latLng(-34.604, -58.382);
            }

            const marker = L.marker(center, {
                icon: L.divIcon({ className: 'empty-icon' }),
                interactive: false,
                pane: 'comunaLabels'
            });

            marker.bindTooltip(`${comunaNumber}`, {
                permanent: true,
                direction: 'center',
                className: 'comuna-tooltip',
                offset: [0, 0],
                pane: 'comunaLabels'
            });

            layer.on('add', () => {
                if (layer._map) {
                    marker.addTo(layer._map);
                }
            });

            const comunaData = dataByComuna.get(comunaNumber.toString());
            const comunaInfo = additionalInfoByComuna.get(comunaNumber.toString());

            const dataInfo: Record<string, number | string> = Object.fromEntries(
                Object.entries((comunaData as unknown as Record<string, number | string>) ?? {})
                    .filter(([key]) => key !== "comuna" && key !== "anio")
                    .map(([key, value]) => [capitalizeKey(key), value])
            );

            const comunaLabel = `Comuna ${comunaNumber}`;
            const matchingDynamicInfo = dynamicInfoByComuna.get(comunaLabel);

            const dynamicInfoFiltered: Record<string, number | string> = matchingDynamicInfo
                ? Object.fromEntries(
                    Object.entries(matchingDynamicInfo)
                        .filter(([key]) => key !== "encabezado" && key !== "anio")
                        .map(([key, value]) => [capitalizeKey(key), value])
                )
                : {};

            const tooltipInfo: Record<string, number | string> = {
                ...(comunaInfo ?? {}),
                ...dataInfo,
                ...dynamicInfoFiltered,
            };

            if (Object.keys(tooltipInfo).length > 0) {
                if (isMobile) {
                    layer.on('click', () => {
                        setSelectedComunaInfo({ comunaNumber, info: tooltipInfo });
                    });
                } else {
                    layer.bindPopup(buildAdditionalInfoContent(comunaNumber, tooltipInfo), {
                        closeButton: true,
                        autoPan: true,
                        maxWidth: 260,
                        className: 'comuna-info-tooltip-wrapper',
                    });
                }

                layer.on('mouseover', () => {
                    layer.setStyle({ weight: 3, fillOpacity: 0.8 });
                    layer.bringToFront();
                });

                layer.on('mouseout', () => {
                    layer.setStyle(styleFeature(feature));
                });
            }
        }
    }, [dataByComuna, additionalInfoByComuna, dynamicInfoByComuna, styleFeature, isMobile]);

    const geoJsonResetKey = useMemo(() => {
        return `geojson-${chartId}-${intervals.join('-')}-${colors.join('-')}-${isMobile}`;
    }, [chartId, intervals, colors, isMobile]);

    return (
        <ResponsiveContainer className='h-[469px] md:h-[600px]'>
            <div className='h-[469px] md:h-[600px]' style={{ position: "relative" }}>
                <div
                    ref={mapRef}
                    id={`my-node-${chartId ?? 'default'}`}
                    className="heatmap-map-wrapper"
                    style={{ width: "100%", height: "100%", position: 'relative' }}
                >
                    <MapContainer
                        key={`map-${chartId}`}
                        style={{ width: "100%", height: "100%" }}
                        center={[-34.619948411233096, -58.44110846308608]}
                        zoom={!isMobile ? 11.5 : 11}
                        scrollWheelZoom={false}
                        preferCanvas={true}
                    >
                        <MapController isMobile={isMobile} />
                        <Pane name="comunaLabels" style={{ zIndex: 550 }} />
                        <TileLayer
                            attribution='&copy; <a href="http://openstreetmap.org">OpenStreetMap</a>'
                            url='https://tile.openstreetmap.org/{z}/{x}/{y}.png'
                        />
                        {intervals.length > 0 && (
                            <GeoJSON
                                key={geoJsonResetKey}
                                data={seg as any}
                                style={styleFeature}
                                onEachFeature={onEachFeature}
                                interactive={true}
                            />
                        )}
                        {mapLayer && mapLayer.length > 0 && (
                            <MapLayers capasMapa={mapLayer} activeCapaIds={activeCapaIds} />
                        )}
                    </MapContainer>

                    {intervals && intervals.length > 0 &&
                        <Legend intervals={intervals} colors={colors} />
                    }
                    {activeCapaIds && activeCapaIds.length > 0 &&
                        <LayersActive activeCapaIds={activeCapaIds} capasMapa={mapLayer ?? []} />
                    }
                    {errorsNoData ? <NotDataMap message={messages.map} /> : null}
                    {isMobile && selectedComunaInfo && (
                        <ComunaInfoModal
                            comunaNumber={selectedComunaInfo.comunaNumber}
                            info={selectedComunaInfo.info}
                            onClose={() => setSelectedComunaInfo(null)}
                        />
                    )}
                </div>
            </div>
        </ResponsiveContainer>
    );
};

export default HeatMap;